import { useSessionState } from '../../shared/hooks/session-state-hook';
import { sessionKey, clearSessionScope } from '../../shared/util/session-storage';
import React, { useContext } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';

import Card from '../../shared/components/UIElements/Card';
import Input from '../../shared/components/FormElements/Input';
import Button from '../../shared/components/FormElements/Button';
import ErrorModal from '../../shared/components/UIElements/ErrorModal';
import ImageUpload from '../../shared/components/FormElements/ImageUploads';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import {
  VALIDATOR_EMAIL,
  VALIDATOR_MINLENGTH,
  VALIDATOR_REQUIRE
} from '../../shared/util/validators';
import { useForm } from '../../shared/hooks/form-hook';
import { AuthContext } from '../../shared/Context/auth-context';
import { useHttpClient } from '../../shared/hooks/http-hook';
import Icon from '../../shared/components/UIElements/Icon';
import './Auth.css';
import { BACKEND_URL } from '../../shared/util/backend-url';

const Auth = () => {
  const history = useHistory();
  const auth = useContext(AuthContext);
  const { userType } = useParams(); // 'doctor' or 'patient' or 'pharmacy'
  const isDoctor = userType === 'doctor';
  const isPatient = userType === 'patient';
  const isPharmacy = userType === 'pharmacy';
  const [isLoginMode, setIsLoginMode] = useSessionState(
    sessionKey('guest', `auth:${userType}:mode`),
    true
  );
  const { isLoading, error, sendRequest, clearError } = useHttpClient();

  const signupInputs = isLoginMode
    ? {}
    : {
        name: { value: '', isValid: false },
        image: { value: null, isValid: false },
        ...(isDoctor ? { membercode: { value: '', isValid: false } } : {}),
        ...(isPatient
          ? {
              age: { value: '', isValid: false },
              gender: { value: '', isValid: false },
              mobilenumber: { value: '', isValid: false }
            }
          : {}),
        ...(isPharmacy
          ? {
              pharmacyName: { value: '', isValid: false },
              licenseNumber: { value: '', isValid: false }
            }
          : {})
      };
  const [formState, inputHandler, setFormData] = useForm(
    {
      email: { value: '', isValid: false },
      password: { value: '', isValid: false },
      ...signupInputs
    },
    false,
    sessionKey('guest', `auth:${userType}:form`)
  );

  const switchModeHandler = () => {
    if (!isLoginMode) {
      const updatedInputs = { ...formState.inputs };
      delete updatedInputs.name;
      delete updatedInputs.image;
      if (isDoctor) delete updatedInputs.membercode;
      if (isPatient) {
        delete updatedInputs.age;
        delete updatedInputs.gender;
        delete updatedInputs.mobilenumber;
      }
      if (isPharmacy) {
        delete updatedInputs.pharmacyName;
        delete updatedInputs.licenseNumber;
      }
      setFormData(
        updatedInputs,
        formState.inputs.email.isValid && formState.inputs.password.isValid
      );
    } else {
      const newInputs = {
        ...formState.inputs,
        name: { value: '', isValid: false },
        image: { value: null, isValid: false }
      };

      if (isDoctor) newInputs.membercode = { value: '', isValid: false };
      if (isPatient) {
        newInputs.age = { value: '', isValid: false };
        newInputs.gender = { value: '', isValid: false };
        newInputs.mobilenumber = { value: '', isValid: false };
      }
      if (isPharmacy) {
        newInputs.pharmacyName = { value: '', isValid: false };
        newInputs.licenseNumber = { value: '', isValid: false };
      }

      setFormData(newInputs, false);
    }
    setIsLoginMode((prev) => !prev);
  };

  const authSubmitHandler = async (event) => {
    event.preventDefault();

    const endpointType = isDoctor ? 'doctor' : isPatient ? 'patient' : 'pharmacy';

    if (isLoginMode) {
      try {
        const responseData = await sendRequest(
          `${BACKEND_URL}/api/${endpointType}/login`,
          'POST',
          JSON.stringify({
            email: formState.inputs.email.value,
            password: formState.inputs.password.value
          }),
          { 'Content-Type': 'application/json' }
        );

        clearSessionScope('guest');
        if (isDoctor) {
          auth.Dlogin(responseData.user, responseData.token);
        } else if (isPatient) {
          auth.Plogin(responseData.user, responseData.token);
        } else {
          auth.Phlogin(responseData.user, responseData.token); // ✅ implement this in your context
        }

        history.push('/');
      } catch (err) {
        console.error(err);
      }
    } else {
      try {
        const formData = new FormData();
        formData.append('name', formState.inputs.name.value);
        formData.append('email', formState.inputs.email.value);
        formData.append('password', formState.inputs.password.value);

        if (formState.inputs.image?.value) {
          formData.append('profileImage', formState.inputs.image.value);
        }

        if (isDoctor) formData.append('membercode', formState.inputs.membercode.value);
        if (isPatient) {
          formData.append('age', formState.inputs.age.value);
          formData.append('gender', formState.inputs.gender.value);
          formData.append('mobilenumber', formState.inputs.mobilenumber.value);
        }
        if (isPharmacy) {
          formData.append('pharmacyName', formState.inputs.pharmacyName.value);
          formData.append('licenseNumber', formState.inputs.licenseNumber.value);
        }

        const responseData = await sendRequest(
          `${BACKEND_URL}/api/${endpointType}/signup`,
          'POST',
          formData
        );

        clearSessionScope('guest');
        if (isDoctor) {
          auth.Dlogin(responseData.user, responseData.token);
        } else if (isPatient) {
          auth.Plogin(responseData.user, responseData.token);
        } else {
          auth.Phlogin(responseData.user, responseData.token);
        }

        history.push('/');
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      <div className="auth-layout">
        <aside className="auth-aside">
          <span className="auth-aside__icon">
            <Icon name={userType} size={36} />
          </span>
          <p className="eyebrow">{userType} workspace</p>
          <h1>
            {isDoctor ? (
              <>
                A clearer view.
                <br />
                More focused care.
              </>
            ) : isPatient ? (
              <>
                Your health story.
                <br />
                Always with you.
              </>
            ) : (
              <>
                Connected care.
                <br />
                From prescription
                <br />
                to pharmacy.
              </>
            )}
          </h1>
          <p>
            {isDoctor
              ? 'Patient history, prescriptions, and clinical notes, together in your everyday workspace.'
              : isPatient
                ? 'Keep your health details, medical history, and reports together in one place.'
                : 'Access prescriptions and stay on top of medication refill follow-ups.'}
          </p>
          <div className="auth-journey">
            <span>
              <Icon name="doctor" />
              Doctor
            </span>
            <i />
            <span>
              <Icon name="patient" />
              Patient
            </span>
            <i />
            <span>
              <Icon name="pharmacy" />
              Pharmacy
            </span>
          </div>
          <div className="auth-aside__footer">
            Curentia <span>Care, connected.</span>
          </div>
        </aside>
        <Card className="authentication">
          <Link className="back-link" to="/">
            <Icon name="back" size={16} /> All roles
          </Link>
          {isLoading && <LoadingSpinner asOverlay />}
          {!isLoginMode && (
            <button type="button" className="modal-back" onClick={switchModeHandler}>
              <Icon name="back" size={16} />
              Back to sign in
            </button>
          )}
          <span className="badge auth-role">
            <Icon name={userType} size={15} />
            {userType} account
          </span>
          <h2>{isLoginMode ? 'Welcome back.' : 'Let’s get you started.'}</h2>
          <p className="auth-description">
            {isLoginMode
              ? 'Sign in to continue to your workspace.'
              : 'Create your account to connect to your care workspace.'}
          </p>
          <form onSubmit={authSubmitHandler}>
            {!isLoginMode && (
              <>
                <Input
                  element="input"
                  id="name"
                  initialValue={formState.inputs.name?.value || ''}
                  initialValid={formState.inputs.name?.isValid || false}
                  type="text"
                  label="Your Name"
                  validators={[VALIDATOR_REQUIRE()]}
                  errorText="Please enter a name."
                  onInput={inputHandler}
                />
                <ImageUpload
                  storageKey={sessionKey('guest', `auth:${userType}:image`)}
                  center
                  id="image"
                  onInput={inputHandler}
                  errorText="Please provide an image"
                />
                {isDoctor && (
                  <Input
                    element="input"
                    id="membercode"
                    initialValue={formState.inputs.membercode?.value || ''}
                    initialValid={formState.inputs.membercode?.isValid || false}
                    type="text"
                    label="Member Code"
                    validators={[VALIDATOR_REQUIRE()]}
                    errorText="Please enter your medical code."
                    onInput={inputHandler}
                  />
                )}
                {isPatient && (
                  <>
                    <Input
                      element="input"
                      id="age"
                      initialValue={formState.inputs.age?.value || ''}
                      initialValid={formState.inputs.age?.isValid || false}
                      type="number"
                      label="Age"
                      validators={[VALIDATOR_REQUIRE()]}
                      errorText="Please enter your age."
                      onInput={inputHandler}
                    />
                    <Input
                      element="input"
                      id="gender"
                      initialValue={formState.inputs.gender?.value || ''}
                      initialValid={formState.inputs.gender?.isValid || false}
                      type="text"
                      label="Gender"
                      validators={[VALIDATOR_REQUIRE()]}
                      errorText="Please enter your gender."
                      onInput={inputHandler}
                    />
                    <Input
                      element="input"
                      id="mobilenumber"
                      initialValue={formState.inputs.mobilenumber?.value || ''}
                      initialValid={formState.inputs.mobilenumber?.isValid || false}
                      type="text"
                      label="Contact Number"
                      validators={[VALIDATOR_REQUIRE()]}
                      errorText="Please enter your contact number."
                      onInput={inputHandler}
                    />
                  </>
                )}
                {isPharmacy && (
                  <>
                    <Input
                      element="input"
                      id="pharmacyName"
                      initialValue={formState.inputs.pharmacyName?.value || ''}
                      initialValid={formState.inputs.pharmacyName?.isValid || false}
                      type="text"
                      label="Pharmacy Name"
                      validators={[VALIDATOR_REQUIRE()]}
                      errorText="Please enter your pharmacy name."
                      onInput={inputHandler}
                    />
                    <Input
                      element="input"
                      id="licenseNumber"
                      initialValue={formState.inputs.licenseNumber?.value || ''}
                      initialValid={formState.inputs.licenseNumber?.isValid || false}
                      type="text"
                      label="License Number"
                      validators={[VALIDATOR_REQUIRE()]}
                      errorText="Please enter your license number."
                      onInput={inputHandler}
                    />
                  </>
                )}
              </>
            )}
            <Input
              element="input"
              id="email"
              initialValue={formState.inputs.email?.value || ''}
              initialValid={formState.inputs.email?.isValid || false}
              type="email"
              label="Email address"
              placeholder="you@example.com"
              autoComplete="email"
              validators={[VALIDATOR_EMAIL()]}
              errorText="Please enter a valid email address."
              onInput={inputHandler}
            />
            <Input
              element="input"
              id="password"
              initialValue={formState.inputs.password?.value || ''}
              initialValid={formState.inputs.password?.isValid || false}
              type="password"
              label="Password"
              placeholder={isLoginMode ? 'Enter your password' : 'At least 5 characters'}
              autoComplete={isLoginMode ? 'current-password' : 'new-password'}
              validators={[VALIDATOR_MINLENGTH(5)]}
              errorText="Please enter a valid password, at least 5 characters."
              onInput={inputHandler}
            />
            <Button type="submit" disabled={!formState.isValid}>
              {isLoginMode ? 'Sign in' : 'Create account'} <Icon name="arrow" size={18} />
            </Button>
          </form>
          <div className="auth-switch">
            <span>{isLoginMode ? 'New to Curentia?' : 'Already have an account?'}</span>
            <button type="button" onClick={switchModeHandler}>
              {isLoginMode ? 'Create an account' : 'Sign in'}
            </button>
          </div>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default Auth;
